import React from 'react';

import { Box, Text } from '@betterment/samba-react';

import { EditNameForm } from './EditNameForm';
import { PurposeSection } from './PurposeSection';

import { SambaAutoLink } from '../../../features/navigation/SambaAutoLink';
import { ModalSection } from '../../modal-section/ModalSection';
import { ModalSectionRow } from '../../modal-section/ModalSectionRow';
import { formatLegalAccountType } from '../helpers';

import type { AccountSettingsUrlBuilders, RenderCloseAccountAction, RenderDeletePurposeAction } from '../contentTypes';
import type { CashAccount } from '../data';

type CashAccountSettingsProps = {
  account: CashAccount;
  currentPath: string;
  urlBuilders: Pick<AccountSettingsUrlBuilders, 'accountDetails' | 'editPortfolio' | 'legalAccounts' | 'editPurpose'>;
  renderDeletePurpose?: RenderDeletePurposeAction;
  renderCloseAccount?: RenderCloseAccountAction;
  purposelessCopy?: string;
  handleNameUpdate?: (newName?: string) => Promise<void>;
};

export const CashAccountSettings: React.FC<CashAccountSettingsProps> = ({
  account,
  currentPath,
  urlBuilders,
  renderDeletePurpose,
  renderCloseAccount,
  purposelessCopy,
  handleNameUpdate,
}) => {
  const { legalAccount } = account;
  const legalAccountType = formatLegalAccountType(legalAccount);
  const hasBeneficiaries = legalAccount.primaryBeneficiaries.length > 0;

  const closeAccountAction =
    renderCloseAccount &&
    renderCloseAccount({
      balance: account.balance,
      purposeName: account.envelope.purpose?.name,
      name: account.name,
      legacyAccountId: account.legacySubAccountId,
    });

  return (
    <Box css={{ display: 'grid', gap: 'space.200' }}>
      <ModalSection header="Account details" cta={urlBuilders.accountDetails}>
        <EditNameForm
          name={account.nameOverride}
          accountDescription={account.accountDescription}
          handleNameUpdate={handleNameUpdate}
        />
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text variant="text50" color="text.weak">
              Account type
            </Text>
            <Text>{legalAccountType}</Text>
          </ModalSectionRow.LeadingContent>
        </ModalSectionRow>
        {!hasBeneficiaries && urlBuilders.legalAccounts && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text variant="text50" color="text.weak">
                Beneficiaries
              </Text>
              <Text>None</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <SambaAutoLink to={urlBuilders.legalAccounts}>Add</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
      </ModalSection>
      <PurposeSection
        account={account}
        currentPath={currentPath}
        urlBuilders={{ editPurpose: urlBuilders.editPurpose }}
        renderDeletePurpose={renderDeletePurpose}
        purposelessCopy={purposelessCopy}
      />
      {closeAccountAction}
    </Box>
  );
};
